import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  LinearProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { ExpandMore as ExpandIcon } from '@mui/icons-material';
import type { SxProps, Theme } from '@mui/system';
import { colors } from 'Constants/Colors';
import useTranslation from 'Hooks/useTranslation';
import type { JobTitleCoursesGroupI } from 'Api/axios/job-title-courses-report-api';

interface Props {
  groups: JobTitleCoursesGroupI[];
  loading: boolean;
  error: string | null;
}

/**
 * Anchos compartidos por la tabla de cada grupo. Al ser porcentajes idénticos
 * en todos los acordeones, las columnas quedan alineadas verticalmente aunque
 * el contenido de cada grupo tenga longitudes muy distintas.
 */
const COLUMN_WIDTHS = { course: '50%', category: '25%', hours: '25%' };

const styles: Record<string, SxProps<Theme>> = {
  accordion: {
    borderRadius: 2,
    border: `1px solid ${colors.GRAY_HR}`,
    boxShadow: 'none',
    overflow: 'hidden',
    mb: 1.5,
    '&::before': { display: 'none' },
  },
  summary: { px: 2, '& .MuiAccordionSummary-content': { my: 1 } },
  groupTitle: { fontSize: 14, fontWeight: 600, color: colors.BLACK },
  groupChip: {
    height: 22,
    fontSize: 11,
    fontWeight: 500,
    bgcolor: colors.BLUE_GRAY,
    color: colors.GRAY_DARKER,
    border: 'none',
  },
  groupChipMuted: {
    height: 22,
    fontSize: 11,
    fontWeight: 500,
    bgcolor: 'transparent',
    color: colors.GRAY_DARK,
    border: `1px dashed ${colors.GRAY_HR}`,
    fontStyle: 'italic',
  },
  details: { p: 0 },
  // Cada grupo es una tabla independiente. Sin `fixed`, el navegador dimensiona
  // las columnas según el contenido de CADA tabla, y un curso de nombre largo
  // desalinea ese grupo respecto a los demás. Con anchos fijos las tres tablas
  // comparten la misma geometría y las columnas quedan a plomo.
  table: { tableLayout: 'fixed' },
  headerCell: {
    bgcolor: colors.BLUE_GRAY,
    fontSize: 11,
    fontWeight: 600,
    color: colors.GRAY_DARK,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
    py: 1,
  },
  courseCell: { fontSize: 13, fontWeight: 500, wordBreak: 'break-word' },
  mutedCell: { fontSize: 12.5, color: colors.GRAY_DARKER, wordBreak: 'break-word' },
  empty: { textAlign: 'center', py: 6, color: colors.GRAY_DARK, fontSize: 13 },
};

/**
 * Lista agrupada por puesto del Reporte de Cursos por Puesto.
 *
 * Mismo lenguaje visual que `SessionsReportGroupedList`: acordeón por grupo con
 * chips de resumen en el encabezado y la tabla de detalle adentro. Los grupos
 * arrancan expandidos para que el reporte se lea de un vistazo sin clics.
 *
 * Presentacional puro: no agrega ni ordena, solo pinta lo que el backend mandó.
 */
export default function JobTitleCoursesGroupedList({ groups, loading, error }: Props) {
  const { t2 } = useTranslation('JobTitleCoursesReport');

  if (loading && groups.length === 0) {
    return <LinearProgress sx={{ height: 3, borderRadius: 99 }} />;
  }

  if (error) {
    return (
      <Box sx={styles.empty}>
        <Typography sx={{ color: colors.RED, fontSize: 13 }}>{error}</Typography>
      </Box>
    );
  }

  if (groups.length === 0) {
    return (
      <Box sx={styles.empty}>
        <Typography>{t2('table.empty')}</Typography>
      </Box>
    );
  }

  return (
    <Box>
      {groups.map(group => {
        // Un "0 h" cuando nadie capturó las horas se leería como "este puesto no
        // requiere entrenamiento". Solo se muestran horas si hay al menos un
        // curso con el dato; si no, el chip lo dice explícitamente.
        const hasHours = group.courses.some(course => course.trainingHours !== null);

        return (
        <Accordion key={group.groupKey} defaultExpanded sx={styles.accordion} disableGutters>
          <AccordionSummary expandIcon={<ExpandIcon />} sx={styles.summary}>
            <Stack direction="row" alignItems="center" gap={1.5} flexWrap="wrap" sx={{ width: '100%' }}>
              <Typography sx={styles.groupTitle}>{group.groupLabel}</Typography>
              <Chip
                label={t2('groupMeta.coursesCount', [String(group.groupMeta.coursesCount)])}
                size="small"
                sx={styles.groupChip}
              />
              <Chip
                label={
                  hasHours
                    ? t2('groupMeta.totalHours', [String(group.groupMeta.totalTrainingHours)])
                    : t2('groupMeta.noHours')
                }
                size="small"
                sx={hasHours ? styles.groupChip : styles.groupChipMuted}
              />
            </Stack>
          </AccordionSummary>
          <AccordionDetails sx={styles.details}>
            <Table size="small" sx={styles.table}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ ...styles.headerCell, width: COLUMN_WIDTHS.course }}>
                    {t2('columns.course')}
                  </TableCell>
                  <TableCell sx={{ ...styles.headerCell, width: COLUMN_WIDTHS.category }}>
                    {t2('columns.category')}
                  </TableCell>
                  <TableCell sx={{ ...styles.headerCell, width: COLUMN_WIDTHS.hours }}>
                    {t2('columns.trainingHours')}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {group.courses.map(course => (
                  <TableRow key={course.id}>
                    <TableCell sx={styles.courseCell}>{course.courseName}</TableCell>
                    <TableCell sx={styles.mutedCell}>
                      {course.categoryName ?? t2('table.noCategory')}
                    </TableCell>
                    <TableCell sx={styles.mutedCell}>
                      {course.trainingHours === null
                        ? t2('table.noTrainingHours')
                        : `${course.trainingHours} h`}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </AccordionDetails>
        </Accordion>
        );
      })}
    </Box>
  );
}
